/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_PAGE_SIZE, NewsGWResponse, RootNewsResponse } from '../../models/news';
import { ErrorPayloadFull } from '../common-actions';

import { checkImageURLs, getNewsRequestOrCacheKey, isNewsCacheTimeValid } from './helpers';
import { NewsState } from './types';

export const INITIAL_STATE: NewsState = {
  isLoading: false,
  latestNews: [],
  newsForSymbols: {},
  error: {},
  newsFetchedTimeByKey: {},
};

const newsSlice = createSlice({
  name: 'news',
  initialState: INITIAL_STATE,
  reducers: {
    getNewsByAccessId(state, action: PayloadAction<NewsGWResponse>) {
      state.isLoading = true;
    },
    getNewsByAccessIdCompleted(state, action: PayloadAction<RootNewsResponse>) {
      const { newsArticles } = action.payload ?? {};
      const { data } = newsArticles ?? {};
      state.latestNews = checkImageURLs((data || []).slice(0, DEFAULT_PAGE_SIZE));

      state.isLoading = false;
      state.newsFetchedTimeByKey[getNewsRequestOrCacheKey()] = Date.now();
    },
    getNewsByAccessIdFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    getSymbolNewsByAccessIdCompleted(state, action) {
      const { newsArticles, customData } = action.payload ?? {};
      const { data } = newsArticles ?? {};
      const { symbol } = customData ?? {};
      state.newsFetchedTimeByKey[getNewsRequestOrCacheKey(symbol)] = Date.now();
      state.newsForSymbols[symbol] = checkImageURLs((data || []).slice(0, DEFAULT_PAGE_SIZE));
      state.isLoading = false;
    },
    getSymbolNewsByAccessIdFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { customData: symbol } = action.payload;
      state.newsForSymbols[symbol] = [];
      state.error = action.payload;
      state.isLoading = false;
    },
    getLatestNews(state, action: PayloadAction<void | undefined>) {
      const isCacheValid = isNewsCacheTimeValid(
        state.newsFetchedTimeByKey[getNewsRequestOrCacheKey()],
      );

      if (isCacheValid) {
        return;
      }

      state.latestNews = [];
      state.isLoading = true;
    },
    getNewsForSymbol(state, action: PayloadAction<string>) {
      const symbol = action.payload;
      const isCacheValid = isNewsCacheTimeValid(
        state.newsFetchedTimeByKey[getNewsRequestOrCacheKey(symbol)],
      );

      if (isCacheValid) {
        return;
      }

      state.newsForSymbols[symbol] = [];
      state.isLoading = true;
    },
    setNewsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
  },
});

export const {
  getNewsByAccessId,
  getNewsByAccessIdCompleted,
  getNewsByAccessIdFailed,
  getSymbolNewsByAccessIdCompleted,
  getSymbolNewsByAccessIdFailed,
  getLatestNews,
  getNewsForSymbol,
  setNewsLoading,
} = newsSlice.actions;

export default newsSlice.reducer;
